import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Heart, Truck, ShieldCheck, PawPrint, Dog, Cat, Bird, Fish, Rabbit, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/store/ProductCard";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/store/Reveal";
import { MembershipSignup } from "@/components/store/MembershipSignup";
import { optImg } from "@/lib/img";

const HERO = "https://static.prod-images.emergentagent.com/jobs/44ea8c31-2f46-4df3-8fb8-8cc27e155b24/images/e8e8946145e9d9c28825612b1e3b2a52f55d421fc05def9463fc7107e7f547cf.jpeg";
const FLATLAY = "https://static.prod-images.emergentagent.com/jobs/44ea8c31-2f46-4df3-8fb8-8cc27e155b24/images/069ac04f17e778cd5ab4e64907430ed15a23412bc13d5b3c42f634d81d7224f9.jpeg";

const SPECIES = [
  { slug: "perro", name: "Perros", icon: Dog, img: "https://images.unsplash.com/photo-1552053831-71594a27632d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { slug: "gato", name: "Gatos", icon: Cat, img: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { slug: "conejos-roedores", name: "Conejos y roedores", icon: Rabbit, img: "https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { slug: "aves", name: "Aves", icon: Bird, img: "https://images.unsplash.com/photo-1552728089-57bdde30beb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { slug: "peces", name: "Peces", icon: Fish, img: "https://images.unsplash.com/photo-1520302630591-fd1c66edc19d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
];

const CATEGORIES = [
  { slug: "alimento", name: "Alimento", emoji: "🍖" },
  { slug: "snacks", name: "Snacks", emoji: "🦴" },
  { slug: "accesorios", name: "Accesorios", emoji: "🎀" },
  { slug: "juguetes", name: "Juguetes", emoji: "🧸" },
  { slug: "higiene", name: "Higiene", emoji: "🧼" },
  { slug: "camas-y-rascadores", name: "Camas & Rascadores", emoji: "🛏️" },
];

const Label = ({ children }) => (
  <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-coral mb-4">{children}</span>
);

export default function Home() {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data)).catch(() => {});
  }, []);

  const visible = products.filter((p) => p.published !== false);
  const featured = (visible.filter((p) => p.featured).length ? visible.filter((p) => p.featured) : visible).slice(0, 6);

  return (
    <div className="overflow-hidden">
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative h-[92vh] min-h-[560px] w-full overflow-hidden">
        <motion.div style={{ scale: imgScale, y: imgY }} className="absolute inset-0">
          <img src={HERO} alt="Perro y gato felices" fetchpriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/45 via-charcoal/5 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-cream/60 via-transparent to-transparent md:from-cream/40" />
        </motion.div>

        <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-10 h-full max-w-6xl mx-auto px-5 flex flex-col justify-center">
          <div className="max-w-xl">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="text-xs font-semibold tracking-[0.3em] uppercase text-charcoal/70 mb-5">
              The Pet Shopping Co · Costa Rica
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="font-display text-[2.6rem] leading-[0.98] sm:text-6xl md:text-7xl font-bold text-charcoal tracking-tight">
              El bienestar<br />de tu mascota,<br /><span className="text-coral">primero.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }} className="mt-6 text-base sm:text-lg text-charcoal/80 leading-relaxed max-w-md">
              Alimento premium y accesorios curados con cariño. Envíos a todo el país y atención personalizada por WhatsApp.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }} className="mt-8 flex flex-wrap gap-3">
              <Link to="/tienda" className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-charcoal text-cream font-semibold hover:bg-coral transition-colors duration-300">
                Explorar la tienda
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/nosotros" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/70 backdrop-blur text-charcoal font-semibold hover:bg-white transition-colors">
                Nuestra historia
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-charcoal/60">
          <span className="text-[10px] tracking-[0.3em] uppercase">Descubre</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-8 bg-charcoal/40" />
        </motion.div>
      </section>

      {/* ============ MARQUEE / TRUST ============ */}
      <section className="border-y border-[#E3E7E2] bg-cream-2">
        <div className="max-w-6xl mx-auto px-5 py-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {[
            { icon: Truck, t: "Envíos a todo CR" },
            { icon: ShieldCheck, t: "Compra segura" },
            { icon: Heart, t: "10% para casas cuna" },
            { icon: Star, t: "+1200 productos" },
          ].map((b, i) => (
            <Reveal key={i} delay={i * 0.05} y={16} className="flex items-center gap-2.5 justify-center sm:justify-start">
              <b.icon className="w-5 h-5 text-coral shrink-0" />
              <span className="text-sm font-semibold text-charcoal">{b.t}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ SPECIES ============ */}
      <section className="max-w-6xl mx-auto px-5 py-20 sm:py-28">
        <Reveal className="max-w-2xl">
          <Label>Compra por especie</Label>
          <h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight leading-tight">
            Todo lo que tu compañero necesita
          </h2>
        </Reveal>
        <StaggerGroup className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {SPECIES.map((s) => (
            <StaggerItem key={s.slug}>
              <Link to={`/tienda?especie=${s.slug}`} data-testid={`species-card-${s.slug}`} className="group relative block aspect-[3/4] rounded-3xl overflow-hidden card-shadow">
                <img src={optImg(s.img, 500)} alt={s.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-center justify-between text-cream">
                  <span className="font-display text-xl font-bold flex items-center gap-2"><s.icon className="w-5 h-5" /> {s.name}</span>
                  <ArrowRight className="w-5 h-5 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* ============ FEATURED ============ */}
      <section className="bg-white border-y border-[#E3E7E2]">
        <div className="max-w-6xl mx-auto px-5 py-20 sm:py-28">
          <Reveal className="flex items-end justify-between gap-4 mb-10">
            <div>
              <Label>Selección destacada</Label>
              <h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight">Favoritos de la casa</h2>
            </div>
            <Link to="/tienda" data-testid="featured-ver-todo-top" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:gap-2.5 transition-all">
              Ver todo <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <StaggerGroup className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {featured.map((p, i) => (
              <StaggerItem key={p.id}><ProductCard product={p} index={i} /></StaggerItem>
            ))}
          </StaggerGroup>
          <div className="mt-10 flex justify-center">
            <Link to="/tienda" data-testid="featured-ver-todo-button" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all card-shadow">
              Ver todo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ BRAND STATEMENT ============ */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-5 py-24 sm:py-36 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Label>Empresa familiar · 7 años</Label>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight leading-[1.05]">
              Cada compra ayuda a un animalito sin hogar
            </h2>
            <p className="mt-6 text-base sm:text-lg text-charcoal/75 leading-relaxed">
              Somos una empresa familiar tica con más de 7 años acompañando a las familias y sus mascotas.
              Donamos el <strong className="text-coral">10% de nuestras utilidades</strong> a casas cuna y programas de cuido animal.
            </p>
            <Link to="/nosotros" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-charcoal text-cream font-semibold hover:bg-coral transition-colors">
              Conoce nuestra misión <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-[2rem] overflow-hidden card-shadow aspect-[4/3]">
              <img src={FLATLAY} alt="Productos premium para mascotas" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ CATEGORIES ============ */}
      <section className="bg-cream-2 border-y border-[#E3E7E2]">
        <div className="max-w-6xl mx-auto px-5 py-20 sm:py-28">
          <Reveal><Label>Categorías</Label><h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight">Explora la tienda</h2></Reveal>
          <StaggerGroup className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-10">
            {CATEGORIES.map((c) => (
              <StaggerItem key={c.slug}>
                <Link to={`/tienda?categoria=${c.slug}`} data-testid={`home-category-${c.slug}`} className="group flex items-center justify-between bg-white rounded-2xl border border-[#E3E7E2] px-5 py-6 hover:border-coral hover:-translate-y-1 transition-all card-shadow">
                  <span className="font-display text-lg font-bold text-charcoal flex items-center gap-3"><span className="text-2xl">{c.emoji}</span> {c.name}</span>
                  <ArrowRight className="w-5 h-5 text-coral -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      {reviews.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 py-20 sm:py-28">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Label>Reseñas</Label>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight">Familias felices en todo el país</h2>
          </Reveal>
          <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {reviews.slice(0, 6).map((r) => (
              <StaggerItem key={r.id}>
                <figure data-testid={`review-${r.id}`} className="h-full bg-white rounded-3xl border border-[#E3E7E2] p-6 card-shadow flex flex-col">
                  <Quote className="w-8 h-8 text-peach mb-3" />
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-coral text-coral" : "text-[#E3E7E2]"}`} />
                    ))}
                  </div>
                  <blockquote className="text-sm text-charcoal/80 leading-relaxed flex-1">"{r.text}"</blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-[#E3E7E2] flex items-center gap-3">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-peach text-coral font-bold font-display">{r.author.charAt(0)}</span>
                    <span><span className="block text-sm font-semibold text-charcoal">{r.author}</span><span className="block text-xs text-muted-moss">{r.location}</span></span>
                  </figcaption>
                </figure>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </section>
      )}

      {/* ============ BLOG PREVIEW ============ */}
      {posts.length > 0 && (
        <section className="bg-white border-y border-[#E3E7E2]">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-28">
            <Reveal className="flex items-end justify-between gap-4 mb-10">
              <div><Label>Blog</Label><h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight">Consejos para tu mascota</h2></div>
              <Link to="/blog" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:gap-2.5 transition-all">Ver blog <ArrowRight className="w-4 h-4" /></Link>
            </Reveal>
            <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.slice(0, 3).map((p) => (
                <StaggerItem key={p.id}>
                  <Link to={`/blog/${p.slug}`} className="group block rounded-3xl overflow-hidden border border-[#E3E7E2] card-shadow hover:-translate-y-1 transition-transform">
                    <div className="aspect-[16/10] overflow-hidden bg-cream-2">
                      <img src={optImg(p.cover_image, 500)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-semibold tracking-wide uppercase text-coral">{p.category}</span>
                      <h3 className="font-display text-lg font-bold text-charcoal mt-1 line-clamp-2 leading-snug">{p.title}</h3>
                      <p className="text-sm text-muted-moss mt-2 line-clamp-2">{p.excerpt}</p>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </section>
      )}

      {/* ============ MEMBERSHIP 5% ============ */}
      <section className="max-w-6xl mx-auto px-5 pt-4 pb-8">
        <Reveal><MembershipSignup /></Reveal>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="max-w-6xl mx-auto px-5 py-24 sm:py-32">
        <Reveal>
          <div className="relative rounded-[2.5rem] overflow-hidden bg-charcoal text-cream px-8 py-16 sm:px-16 sm:py-24 text-center">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #E08A7E 0, transparent 40%), radial-gradient(circle at 80% 70%, #B9D8D4 0, transparent 40%)" }} />
            <div className="relative">
              <PawPrint className="w-10 h-10 mx-auto text-coral mb-6" />
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
                ¿Listo para consentir a tu mejor amigo?
              </h2>
              <p className="mt-4 text-cream/70 max-w-md mx-auto">Arma tu carrito y coordina la entrega y el pago por WhatsApp en minutos.</p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/tienda" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-coral text-white font-semibold hover:bg-coral-dark transition-colors">Ir a la tienda <ArrowRight className="w-4 h-4" /></Link>
                <a href={`https://wa.me/${settings.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cream/10 text-cream font-semibold hover:bg-cream/20 transition-colors">Escríbenos por WhatsApp</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
